import { createFileRoute } from '@tanstack/react-router'
import { useState, ReactNode } from 'react'
import { Tabs, TabsContent } from '@/src/components/ui/tabs'
import {
  EnvelopeSimpleIcon,
  ClockIcon,
  TreeStructureIcon,
  LinkIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react'
import { EmailSettingsTab } from '@/src/components/admin/settings/EmailSettingsTab'
import { EnvironmentalSettingsTab } from '@/src/components/admin/settings/EnvironmentalSettingsTab'
import { RateLimitsTab } from '@/src/components/admin/settings/RateLimitsTab'
import { WorkflowUrlAllowlistTab } from '@/src/components/admin/settings/WorkflowUrlAllowlistTab'
import { CronJobsTab } from '@/src/components/admin/settings/CronJobsTab'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { ToolbarTabButton } from '@/src/components/layout/ToolbarTabToggle'

export const Route = createFileRoute('/_manage/manage/admin/settings')({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const requestedTab = search.tab as string | undefined
    const allowedTabs = ['email', 'environmental', 'rate-limits', 'url-allowlist', 'cron-jobs']

    return {
      tab: requestedTab && allowedTabs.includes(requestedTab) ? requestedTab : 'email',
    }
  },
})

function SettingsPage() {
  const navigate = Route.useNavigate()
  const { tab } = Route.useSearch()
  const [toolbarActions, setToolbarActions] = useState<ReactNode>(null)

  const handleTabChange = (value: string) => {
    setToolbarActions(null)
    navigate({ search: { tab: value } })
  }

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <div className="flex gap-1">
              <ToolbarTabButton active={tab === 'email'} onClick={() => handleTabChange('email')}>
                <EnvelopeSimpleIcon className="h-4 w-4" />
                Email Settings
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'environmental'} onClick={() => handleTabChange('environmental')}>
                <TreeStructureIcon className="h-4 w-4" />
                Environmental Settings
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'rate-limits'} onClick={() => handleTabChange('rate-limits')}>
                <ClockIcon className="h-4 w-4" />
                Rate Limits
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'url-allowlist'} onClick={() => handleTabChange('url-allowlist')}>
                <LinkIcon className="h-4 w-4" />
                URL Allowlist
              </ToolbarTabButton>
              <ToolbarTabButton active={tab === 'cron-jobs'} onClick={() => handleTabChange('cron-jobs')}>
                <ClockCounterClockwiseIcon className="h-4 w-4" />
                Cron Jobs
              </ToolbarTabButton>
          </div>
        </AdminToolbar.Left>
        <AdminToolbar.Right key={tab}>
          {toolbarActions}
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="container">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsContent value="email">
            <EmailSettingsTab onToolbarActionsChange={setToolbarActions} />
          </TabsContent>

          <TabsContent value="environmental">
            <EnvironmentalSettingsTab onToolbarActionsChange={setToolbarActions} />
          </TabsContent>

          <TabsContent value="rate-limits">
            <RateLimitsTab onToolbarActionsChange={setToolbarActions} />
          </TabsContent>

          <TabsContent value="url-allowlist">
            <WorkflowUrlAllowlistTab onToolbarActionsChange={setToolbarActions} />
          </TabsContent>

          <TabsContent value="cron-jobs">
            <CronJobsTab onToolbarActionsChange={setToolbarActions} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
